import { FontAwesome, MaterialIcons } from "@expo/vector-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Center,
  Divider,
  HStack,
  Icon,
  InputLeftAddon,
  Pressable,
  Text,
  VStack,
} from "native-base";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { TextInputMask } from "react-native-masked-text";
import * as yup from "yup";

import ExternalImage from "@/components/atoms/ExternalImage";
import FormButton from "@/components/atoms/FormButton";
import TextInput from "@/components/atoms/TextInput";
import FormField from "@/components/molecules/FormField";
import Modal from "@/components/molecules/Modal";
import { useStorePaymentMethods } from "@/hooks/domain/stores";
import { OrderPaymentMethodTypesEnum, OrderPaymentMethodsEnum } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";
import { StorePaymentMethod } from "@/interfaces/domain/StorePaymentMethod";
import analytics from "@/services/analytics";
import { formatToCurrency } from "@/utils/helpers";

import { formatPaymentTypeName, mapPaymentMethodToOrderPaymentMethod } from "../functions";
import { ModalPropos, PaymentMethod } from "../types";

const PaymentMethodModal = ({
  isOpen,
  currentValue,
  store,
  orderTotal,
  onClose,
  onSubmit,
}: ModalPropos<PaymentMethod> & { store: Store; orderTotal: number }) => {
  const { data } = useStorePaymentMethods(store?._id, {
    enabled: !!store?._id,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [value, setValue] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ money: number }>({
    resolver: yupResolver(
      yup.object({
        money: yup
          .number()
          .typeError("Valor é obrigatório")
          .min(orderTotal - 0.01, "O valor deve ser maior ou igual ao total da compra")
          .required("Valor é obrigatório"),
      })
    ),
    defaultValues: {
      money: currentValue?.clientMoney,
    },
  });

  const onSelectMethod = (method: StorePaymentMethod) => {
    const payload = {
      method: OrderPaymentMethodsEnum.PAY_IN_DELIVERY,
      type: mapPaymentMethodToOrderPaymentMethod(method.tipo),
      description: method.bandeira,
    };
    analytics.logEvent("added_payment_method", payload);
    onSubmit({
      id: method._id,
      ...payload,
    });
    onClose();
  };

  const onSelectMoneyMethod = ({ money: value }: { money: number }) => {
    const money = Number(value);
    const change = money - orderTotal;
    const payload = {
      method: OrderPaymentMethodsEnum.PAY_IN_DELIVERY,
      type: OrderPaymentMethodTypesEnum.CASH,
      description: formatToCurrency(money),
    };
    analytics.logEvent("added_payment_method", payload);
    onSubmit({
      id: "money-payment",
      ...payload,
      isMoneyPayment: true,
      clientChange: change,
      clientMoney: money,
    });
    setValue("");
    onClose();
    setModalVisible(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        Header="Método de pagamento"
        Body={
          <VStack h="600px" mb="32">
            <Text py="2">Escolha uma das opções aceitas por este estabelecimento</Text>
            <Text py="4" fontSize="md" bold>
              Pagamento na entrega
            </Text>
            <Divider />
            {store?.metodo_pagamento?.aceitaDinheiro && (
              <Pressable onPress={() => setModalVisible(true)}>
                <HStack alignItems="center" justifyContent="space-between">
                  <HStack py="2" space="4" alignItems="center">
                    <Center w="56px" h="56px">
                      <Icon as={<FontAwesome name="money" />} size="4xl" color="green.800" />
                    </Center>
                    <Text bold={currentValue?.isMoneyPayment}>Dinheiro</Text>
                  </HStack>
                  <Box bgColor="secondary.400" rounded="full" p="1">
                    <Icon
                      as={<MaterialIcons name="arrow-forward-ios" />}
                      color="primary.600"
                      size="md"
                    />
                  </Box>
                </HStack>
                <Divider />
              </Pressable>
            )}
            {!!data?.length &&
              data.map((paymentMethod) => (
                <Pressable key={paymentMethod._id} onPress={() => onSelectMethod(paymentMethod)}>
                  <HStack alignItems="center" justifyContent="space-between">
                    <HStack py="2" space="4" alignItems="center">
                      <Box>
                        <ExternalImage
                          source={{ uri: paymentMethod?.logo }}
                          width={56}
                          height={56}
                          contentFit="contain"
                        />
                      </Box>
                      <Text
                        textTransform="capitalize"
                        bold={currentValue?.id === paymentMethod?._id}>
                        {formatPaymentTypeName(paymentMethod.tipo)} - {paymentMethod.bandeira}
                      </Text>
                    </HStack>
                    <Box bgColor="secondary.400" rounded="full" p="1">
                      <Icon
                        as={<MaterialIcons name="arrow-forward-ios" />}
                        color="primary.600"
                        size="md"
                      />
                    </Box>
                  </HStack>
                  <Divider />
                </Pressable>
              ))}
          </VStack>
        }
      />
      <Modal
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        Header="Valor em dinheiro"
        Body={
          <VStack alignItems="center">
            <Text fontSize="md" py="2">
              Valor total do seu pedido: <Text bold>{formatToCurrency(orderTotal)}</Text>
            </Text>
            <Text fontSize="md" textAlign="center" py="2">
              Digite quanto vai pagar em dinheiro. Caso não precise de troco, digite o valor total
              da compra.
            </Text>
            <HStack alignItems="center">
              <Text px="2" pt="4">
                R$
              </Text>
              <Box flex={1}>
                <FormField
                  name="money"
                  control={control}
                  label=""
                  error={errors?.money}
                  render={(value, onChange) => (
                    <TextInputMask
                      value={
                        Number(value) === currentValue?.clientMoney
                          ? formatToCurrency(value).replace("R$ ", "")
                          : String(value)
                      }
                      onChangeText={(_, rawText) => onChange(rawText)}
                      customTextInput={TextInput}
                      includeRawValueInChangeText={true}
                      type={"money"}
                      options={{
                        precision: 2,
                        separator: ",",
                        unit: "",
                        delimiter: ".",
                      }}
                      placeholder="0,00"
                    />
                  )}
                />
              </Box>
            </HStack>
            {!!value && Number(value) > orderTotal && (
              <Text fontSize="md" py="2">
                Troco: <Text bold>{formatToCurrency(Number(value) - orderTotal)}</Text>
              </Text>
            )}
          </VStack>
        }
        Footer={
          <VStack w="full">
            <FormButton mt="4" onPress={handleSubmit(onSelectMoneyMethod)}>
              Continuar
            </FormButton>
            <FormButton
              colorScheme="white"
              textColor="primary.700"
              my="4"
              shadow="2"
              onPress={() => setModalVisible(false)}>
              Cancelar
            </FormButton>
          </VStack>
        }
      />
    </>
  );
};

export default PaymentMethodModal;
